'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Save, AlertCircle, Wand2, Loader2, Star, Cloud, CloudOff, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '@/hooks/useAuth';
import {
  createAttendanceTemplate,
  updateAttendanceTemplate,
  type AttendanceTemplateDTO,
  type AttendanceTemplateContent,
} from '@/services/attendanceTemplates';
import {
  clearAttendanceMaskDraft,
  createDebouncedSave,
  loadAttendanceMaskDraft,
  saveAttendanceMaskDraft,
  shouldPreferLocalDraft,
  type AttendanceMaskDraft,
  type AttendanceMaskDraftTabId,
} from '@/lib/attendanceMaskDraft';

interface AttendanceMaskEditorProps {
  template?: AttendanceTemplateDTO;
  onClose: () => void;
  onSaved: () => void;
}

type EditorTabId = AttendanceMaskDraftTabId;

type DraftSyncStatus = 'idle' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DEBOUNCE_MS = 700;

const defaultContent: AttendanceTemplateContent = {
  chiefComplaint: '',
  hda: '',
  physicalExam: '',
  hypothesis: '',
  conduct: '',
  observations: '',
};

function normalizeQuillHtml(html: string): string {
  return html === '<p><br></p>' ? '' : html;
}

function buildEditableContent(content: AttendanceTemplateContent): AttendanceTemplateContent {
  return {
    chiefComplaint: content.chiefComplaint || '',
    hda: content.hda || '',
    physicalExam: content.physicalExam || '',
    hypothesis: content.hypothesis || '',
    conduct: content.conduct || '',
    observations: content.observations || '',
  };
}

export function AttendanceMaskEditor({ template, onClose, onSaved }: AttendanceMaskEditorProps) {
  const { user } = useAuth();
  const doctorId = user?.id || '';

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [content, setContent] = useState<AttendanceTemplateContent>(defaultContent);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTabId>('chiefComplaint');
  const [draftStatus, setDraftStatus] = useState<DraftSyncStatus>('idle');
  const [hydrated, setHydrated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const quillRef = useRef<ReactQuill>(null);
  const skipAutosaveRef = useRef(true);
  const nameRef = useRef(name);
  const categoryRef = useRef(category);
  const isFeaturedRef = useRef(isFeatured);
  const contentRef = useRef(content);
  const activeTabRef = useRef(activeTab);

  nameRef.current = name;
  categoryRef.current = category;
  isFeaturedRef.current = isFeatured;
  contentRef.current = content;
  activeTabRef.current = activeTab;

  const templateId = template?.id ?? null;

  const buildDraftSnapshot = useCallback((): AttendanceMaskDraft => {
    return {
      doctorId,
      templateId,
      updatedAt: new Date().toISOString(),
      name: nameRef.current,
      category: categoryRef.current,
      isFeatured: isFeaturedRef.current,
      content: buildEditableContent(contentRef.current),
      activeTab: activeTabRef.current,
    };
  }, [doctorId, templateId]);

  const debouncedDraft = useMemo(
    () =>
      createDebouncedSave(AUTOSAVE_DEBOUNCE_MS, (draft) => {
        try {
          setDraftStatus('saving');
          saveAttendanceMaskDraft(draft);
          setDraftStatus('saved');
        } catch {
          setDraftStatus('error');
        }
      }),
    [],
  );

  const scheduleAutosave = useCallback(() => {
    if (!doctorId || !hydrated) return;
    setIsDirty(true);
    setDraftStatus('saving');
    debouncedDraft.schedule(buildDraftSnapshot());
  }, [doctorId, hydrated, debouncedDraft, buildDraftSnapshot]);

  const flushActiveTabFromQuill = useCallback((): AttendanceTemplateContent => {
    let nextContent = { ...contentRef.current };
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor) {
        const currentHtml = normalizeQuillHtml(editor.root.innerHTML);
        nextContent = {
          ...nextContent,
          [activeTabRef.current]: currentHtml,
        };
        contentRef.current = nextContent;
        setContent(nextContent);
      }
    }
    return buildEditableContent(nextContent);
  }, []);

  // Hidratação inicial: template do servidor vs rascunho local
  useEffect(() => {
    if (!doctorId) {
      setHydrated(true);
      return;
    }

    const draft = loadAttendanceMaskDraft(doctorId, templateId);
    const preferDraft = shouldPreferLocalDraft(draft, template?.updatedAt);

    if (preferDraft && draft) {
      setName(draft.name || '');
      setCategory(draft.category || '');
      setIsFeatured(!!draft.isFeatured);
      setContent({ ...defaultContent, ...buildEditableContent(draft.content || {}) });
      if (draft.activeTab) setActiveTab(draft.activeTab);
      setDraftStatus('saved');
      setIsDirty(true);
      toast.message('Rascunho restaurado', {
        description: 'Continuamos de onde você parou nesta máscara.',
      });
    } else if (template) {
      setName(template.name);
      setCategory(template.category || '');
      setIsFeatured(template.isFeatured);
      setContent({
        ...defaultContent,
        ...buildEditableContent(template.content || {}),
      });
      setDraftStatus('idle');
      setIsDirty(false);
    } else {
      setName('');
      setCategory('');
      setIsFeatured(false);
      setContent(defaultContent);
      setDraftStatus('idle');
      setIsDirty(false);
    }

    skipAutosaveRef.current = true;
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hidratar uma vez por abertura do editor/contexto
  }, [doctorId, templateId]);

  // Autosave quando campos mudam (após hidratação); ignora o ciclo inicial de restore
  useEffect(() => {
    if (!hydrated) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    scheduleAutosave();
  }, [name, category, isFeatured, content, activeTab, hydrated, scheduleAutosave]);

  // Cleanup debounce ao desmontar
  useEffect(() => {
    return () => {
      debouncedDraft.cancel();
    };
  }, [debouncedDraft]);

  // Aviso ao sair com alterações pendentes
  useEffect(() => {
    if (!isDirty) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const handleContentChange = (field: EditorTabId, value: string) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (newTab: EditorTabId) => {
    flushActiveTabFromQuill();
    setActiveTab(newTab);
  };

  const handleClose = () => {
    flushActiveTabFromQuill();
    if (doctorId) {
      try {
        debouncedDraft.flush(buildDraftSnapshot());
        setDraftStatus('saved');
      } catch {
        setDraftStatus('error');
      }
    }
    onClose();
  };

  const handleSave = async () => {
    const editableContent = flushActiveTabFromQuill();
    const currentName = nameRef.current.trim();

    if (!currentName) {
      toast.error('O nome da máscara é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: currentName,
        category: categoryRef.current.trim() || undefined,
        content: editableContent,
        isFeatured: isFeaturedRef.current,
      };

      if (template) {
        const res = await updateAttendanceTemplate(template.id, payload);
        if (res.success) {
          clearAttendanceMaskDraft(doctorId, templateId);
          debouncedDraft.cancel();
          setIsDirty(false);
          setDraftStatus('idle');
          toast.success('Máscara atualizada com sucesso!');
          onSaved();
        } else {
          toast.error(res.error || 'Erro ao atualizar máscara');
        }
      } else {
        const res = await createAttendanceTemplate(payload);
        if (res.success) {
          clearAttendanceMaskDraft(doctorId, null);
          debouncedDraft.cancel();
          setIsDirty(false);
          setDraftStatus('idle');
          toast.success('Máscara criada com sucesso!');
          onSaved();
        } else {
          toast.error(res.error || 'Erro ao criar máscara');
        }
      }
    } catch {
      toast.error('Falha de comunicação ao salvar a máscara');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: Array<{ id: EditorTabId; label: string }> = [
    { id: 'chiefComplaint', label: 'Queixas' },
    { id: 'hda', label: 'HDA' },
    { id: 'physicalExam', label: 'Exame Físico' },
    { id: 'hypothesis', label: 'Hipótese' },
    { id: 'conduct', label: 'Conduta' },
    { id: 'observations', label: 'Observações' },
  ];

  const currentEditorValue = content[activeTab] || '';

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  };

  const draftStatusLabel =
    draftStatus === 'saving'
      ? 'Salvando rascunho…'
      : draftStatus === 'saved'
        ? 'Rascunho salvo'
        : draftStatus === 'error'
          ? 'Erro ao salvar rascunho'
          : 'Rascunho local';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 md:p-6">
      <div className="bg-white dark:bg-neutral-900 w-full max-w-5xl max-h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-800 flex items-center justify-between bg-gray-50/50 dark:bg-neutral-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center text-teal-600">
              <Wand2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {template ? 'Editar Máscara' : 'Nova Máscara de Atendimento'}
              </h2>
              <p className="text-sm text-gray-500">
                Configure os blocos clínicos para uso no prontuário.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                draftStatus === 'error'
                  ? 'border-red-200 text-red-600 bg-red-50 dark:bg-red-950/30 dark:border-red-900'
                  : draftStatus === 'saving'
                    ? 'border-amber-200 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900'
                    : draftStatus === 'saved'
                      ? 'border-teal-200 text-teal-700 bg-teal-50 dark:bg-teal-950/30 dark:border-teal-900'
                      : 'border-gray-200 text-gray-500 bg-white dark:bg-neutral-800 dark:border-neutral-700'
              }`}
              title={draftStatusLabel}
            >
              {draftStatus === 'saving' ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : draftStatus === 'error' ? (
                <CloudOff className="w-3 h-3" />
              ) : draftStatus === 'saved' ? (
                <Check className="w-3 h-3" />
              ) : (
                <Cloud className="w-3 h-3" />
              )}
              {draftStatusLabel}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="rounded-full text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">
                Nome da Máscara <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Ex: Primeira Consulta Cardiológica"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white dark:bg-neutral-800"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category" className="text-gray-700 dark:text-gray-300">
                Especialidade / Categoria
              </Label>
              <Input
                id="category"
                placeholder="Ex: Cardiologia"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-white dark:bg-neutral-800"
              />
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-400">
              <p className="font-semibold mb-1">Dica de preenchimento</p>
              <p>
                Os blocos que você deixar vazios <strong>não sobrescreverão</strong> o que o médico digitar no
                prontuário. O rascunho das abas é salvo automaticamente neste dispositivo até você clicar em{' '}
                {template ? 'Salvar Alterações' : 'Criar Máscara'}.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-neutral-700 rounded-xl overflow-hidden flex flex-col md:flex-row h-[400px]">
            <div className="w-full md:w-56 bg-gray-50 dark:bg-neutral-800/50 border-b md:border-b-0 md:border-r border-gray-200 dark:border-neutral-700 flex flex-row md:flex-col overflow-x-auto md:overflow-y-auto">
              {tabs.map((tab) => {
                const isFilled = (content[tab.id]?.length || 0) > 0;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center justify-between w-full text-left px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap md:whitespace-normal
                      ${
                        activeTab === tab.id
                          ? 'bg-white dark:bg-neutral-800 text-teal-600 border-l-2 border-teal-500'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 border-l-2 border-transparent'
                      }`}
                  >
                    {tab.label}
                    {isFilled && <span className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 bg-white dark:bg-neutral-900 p-0 flex flex-col">
              <div className="p-3 border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/20">
                <h3 className="font-medium text-gray-700 dark:text-gray-200">
                  {tabs.find((t) => t.id === activeTab)?.label}
                </h3>
              </div>
              <div className="flex-1 [&_.quill]:h-full [&_.ql-container]:h-[calc(100%-42px)] [&_.ql-toolbar]:rounded-none [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-container]:border-none [&_.ql-editor]:text-base">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={currentEditorValue}
                  onChange={(val: string) => handleContentChange(activeTab, val)}
                  modules={modules}
                  placeholder={`Digite a estrutura padrão para ${tabs.find((t) => t.id === activeTab)?.label.toLowerCase()}...`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Switch id="featured" checked={isFeatured} onCheckedChange={setIsFeatured} />
            <Label htmlFor="featured" className="text-sm font-medium flex items-center gap-1 cursor-pointer">
              <Star className="w-4 h-4 text-amber-500" />
              Destacar na lista
            </Label>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button variant="outline" onClick={handleClose} className="flex-1 md:flex-none">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 md:flex-none bg-teal-600 hover:bg-teal-700 text-white gap-2"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {template ? 'Salvar Alterações' : 'Criar Máscara'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
